import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Award, Heart, Shield, Sparkles } from 'lucide-react';

export const HeroSlider = ({ onBookAppointment }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Your Smile Journey Begins Here",
      subtitle: "Where Expertise Meets Compassion",
      description: "16+ years of transforming smiles with precision, care, and cutting-edge dental technology. Dr. Chandrashekhar Harale brings world-class dental care to Kolhapur.",
      image: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?q=80&w=2074&auto=format&fit=crop",
      icon: <Sparkles className="w-8 h-8" />,
      stats: "5000+ Happy Smiles",
      color: "from-cyan-500 to-blue-600"
    },
    {
      title: "Certified BPS Denture Specialist",
      subtitle: "Advanced Precision. Natural Results.",
      description: "First certified BPS Denture clinic in Kolhapur. Experience dentures that look natural, feel comfortable, and restore your confidence completely.",
      image: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?q=80&w=2070&auto=format&fit=crop",
      icon: <Award className="w-8 h-8" />,
      stats: "10+ Years BPS Expertise",
      color: "from-blue-500 to-indigo-600"
    },
    {
      title: "Family Dental Care Excellence",
      subtitle: "Complete Care for Every Generation",
      description: "From children to seniors, we provide comprehensive dental solutions with gentle care. Modern technology meets traditional values at Vijaya Dental Clinic.",
      image: "https://images.unsplash.com/photo-1609557927087-f9cf8e88de18?q=80&w=2070&auto=format&fit=crop",
      icon: <Heart className="w-8 h-8" />,
      stats: "Trusted by 1000+ Families",
      color: "from-indigo-500 to-purple-600"
    },
    {
      title: "Pain-Free Modern Dentistry",
      subtitle: "Advanced Care. Zero Anxiety.",
      description: "State-of-the-art equipment, latest techniques, and a comfortable environment ensure your dental visit is stress-free and pleasant every single time.",
      image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?q=80&w=2068&auto=format&fit=crop",
      icon: <Shield className="w-8 h-8" />,
      stats: "100% Sterilized & Safe",
      color: "from-purple-500 to-pink-600"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000); // Slower: 7 seconds per slide
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-screen min-h-[500px] max-h-[900px] w-full overflow-hidden" data-testid="hero-slider">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src={slides[currentSlide].image}
              alt={slides[currentSlide].title}
              className="w-full h-full object-cover object-center"
            />
            <div className={`absolute inset-0 bg-gradient-to-r ${slides[currentSlide].color} opacity-90`}></div>
          </div>

          {/* Content */}
          <div className="relative h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-3xl">
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                    {slides[currentSlide].icon}
                  </div>
                  <div className="px-4 sm:px-6 py-2 rounded-full bg-white/20 backdrop-blur-md">
                    <span className="text-white font-semibold text-xs sm:text-sm">
                      {slides[currentSlide].stats}
                    </span>
                  </div>
                </motion.div>

                <motion.p
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="text-white/90 text-sm sm:text-lg md:text-xl font-medium mb-3 sm:mb-4 tracking-wide uppercase"
                >
                  {slides[currentSlide].subtitle}
                </motion.p>

                <motion.h1
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="font-bold text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-4 sm:mb-6"
                >
                  {slides[currentSlide].title}
                </motion.h1>

                <motion.p
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="text-white/90 text-base sm:text-xl md:text-2xl leading-relaxed mb-6 sm:mb-10 pr-4"
                >
                  {slides[currentSlide].description}
                </motion.p>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
                >
                  <button
                    onClick={onBookAppointment}
                    className="px-6 sm:px-10 py-3 sm:py-5 rounded-full bg-white text-slate-900 font-bold text-base sm:text-lg shadow-2xl hover:shadow-white/20 hover:scale-105 transition-all duration-300 text-center"
                    data-testid="slider-book-appointment"
                  >
                    Book Your Appointment
                  </button>
                  <a
                    href="tel:09975727098"
                    className="px-6 sm:px-10 py-3 sm:py-5 rounded-full border-2 border-white text-white font-bold text-base sm:text-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-300 text-center"
                    data-testid="slider-call-now"
                  >
                    Call: 099757 27098
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
        data-testid="slider-prev"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 sm:w-8 sm:h-8" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
        data-testid="slider-next"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 sm:w-8 sm:h-8" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? 'w-8 sm:w-12 h-2 sm:h-3 bg-white'
                : 'w-2 sm:w-3 h-2 sm:h-3 bg-white/40 hover:bg-white/60'
            }`}
            data-testid={`slider-dot-${index}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
        className="absolute bottom-8 right-4 sm:right-8 hidden lg:flex flex-col items-center gap-2 text-white/70"
      >
        <span className="text-sm font-medium tracking-wider rotate-90 origin-center">SCROLL</span>
        <div className="w-px h-16 bg-gradient-to-b from-white/70 to-transparent"></div>
      </motion.div>
    </div>
  );
};
